import type { Artifact } from "@shared";
import { reviewableArtifacts } from "./reviewable";
import { ReviewableTable, type RenderCell, type RenderSimpleRow } from "./ReviewableTable";

export interface ArtifactPresentation {
  title?: string;
  subtitle?: React.ReactNode;
  renderCell?: RenderCell;
  renderSimpleRow?: RenderSimpleRow;
  sortedLabel?: string;
  /** Override persisted column labels by key — the frontend owns headers so a
   *  relabel takes effect immediately, even on artifacts saved with old labels. */
  columnLabels?: Record<string, string>;
  /** The market the metrics are scoped to (e.g. "Canada"), shown top-right. */
  location?: string;
}

export interface ArtifactRendererProps {
  artifacts: Artifact[] | undefined;
  /** Approve handoff (transport stays with the caller, like InterruptCard.onResponse). */
  onApprove: (artifact: Artifact) => void;
  /** Block controls while a run streams. */
  disabled?: boolean;
  /** Per-artifact presentation; keyed by id. */
  present?: (artifact: Artifact) => ArtifactPresentation;
}

/**
 * Renders the inline-renderable (reviewable) artifacts from the channel, each
 * dispatched by `type`. v1 handles `table` → <ReviewableTable>. Display-only
 * artifacts are filtered out (they belong in the future ArtifactsTable/sidebar).
 */
export function ArtifactRenderer({ artifacts, onApprove, disabled, present }: ArtifactRendererProps) {
  const inline = reviewableArtifacts(artifacts);
  if (inline.length === 0) return null;

  // Full width: a reviewable table is a workbench, not a chat bubble — it
  // takes every pixel its column offers (the column itself sets the limit).
  return (
    <div className="space-y-4">
      {inline.map((artifact) => {
        const p = present?.(artifact) ?? {};
        switch (artifact.type) {
          case "keywords":
            return (
              <ReviewableTable
                key={artifact.id}
                artifact={artifact}
                onApprove={onApprove}
                disabled={disabled}
                title={p.title}
                subtitle={p.subtitle}
                renderCell={p.renderCell}
                renderSimpleRow={p.renderSimpleRow}
                sortedLabel={p.sortedLabel}
                columnLabels={p.columnLabels}
                location={p.location}
              />
            );
          default:
            return null;
        }
      })}
    </div>
  );
}
